import { compactString, ROLE } from '@talos/kyoko';
import { ALL_ROLE_ITEMS, DEFAULT_API_KEY_ROLES } from '../../../tokens/roles';

// for quick lookup by role option value. use all possible roles for this
export const roleOptionsMap = new Map(ALL_ROLE_ITEMS.map(roleItem => [roleItem.value, roleItem]));
export const isSelectionLocked = (role: ROLE) => role === ROLE.READ_ONLY;
export const getRoleLabel = (role: ROLE) => roleOptionsMap.get(role)?.label || '';
export const getRoleDescription = (role: ROLE) => roleOptionsMap.get(role)?.value || '';

export const getInitialAPIKeyRoles = (apiKeyRoles: ROLE[] | undefined) => {
  // Filter out ROLE.NONE and ensure READ_ONLY is always present
  const roles = (apiKeyRoles ?? []).filter(role => role !== ROLE.NONE);
  const rolesSet = new Set([ROLE.READ_ONLY, ...roles]);
  return [...rolesSet];
};

const isValidIP = (ip: string) => {
  const match = ip.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  return match != null && [match[1], match[2], match[3], match[4]].every(n => parseInt(n) <= 255);
};

export const validateWhitelistedIPs = (value: string) => {
  if (value == null) {
    return true;
  }
  return value.split(/,|\n/).compact(true).map(compactString).every(isValidIP);
};

export const HIDDEN_SECRETS_PLACEHOLDER = 'HIDDEN_SECRETS_ONLY';

export const getPossibleApiKeyRoles = ({
  userRoles,
  allowedRoles = DEFAULT_API_KEY_ROLES,
}: {
  userRoles: ROLE[] | null | undefined;
  allowedRoles?: ROLE[] | undefined;
}): ROLE[] => {
  const safeUserRoles = userRoles ?? [];
  const userRolesSet = new Set(safeUserRoles).add(ROLE.READ_ONLY); // read only always present, either explicitly or implicitly.

  let roleItems = ALL_ROLE_ITEMS
    // [sc-43673] Remove None from API keys capabilities menu
    // Existing Users with the role will still see the option to remove from their capabilities.
    .filter(roleItem => roleItem.value !== ROLE.NONE);

  // Filter by allowed roles (default is applied if undefined)
  if (allowedRoles.length > 0) {
    const allowedRolesSet = new Set(allowedRoles);
    roleItems = roleItems.filter(roleItem => allowedRolesSet.has(roleItem.value));
  }

  // Only allow roles the user has
  const result = roleItems.filter(roleItem => userRolesSet.has(roleItem.value)).map(roleItem => roleItem.value);

  // Always ensure READ_ONLY is present if user has it (which they always do)
  if (!result.includes(ROLE.READ_ONLY)) {
    result.push(ROLE.READ_ONLY);
  }

  return result;
};
